/**
 * The formula reference, for v2.
 *
 * v1 had one and v2 did not, which matters more here than it did there: v2
 * splits COGS, Indirect Costs and Fixed Assets into their own tabs, so a
 * figure on the P&L is very often decided somewhere else entirely. The panel
 * exists to answer "where does this number come from" without reading the
 * engine.
 *
 * Not v1's component. It is a separate application and its list is wrong for
 * v2 in several places - see the note at the top of formulas.ts.
 *
 * Search matters more than it looks: the reason to open this is usually one
 * line, not a tab, and the line you want is frequently on a tab you would not
 * have thought to open. So searching spans every tab and says which one each
 * hit came from.
 */

import { useMemo, useState } from 'react';
import {
  TAB_LABELS,
  type FormulaEntry,
  type FormulaSection,
  type TabKey,
} from './formulaTypes';

/** One group in the panel: an accounting tab, or the valuation's single list. */
export interface FormulaGroup {
  key: string;
  label: string;
  sections: FormulaSection[];
}

function SourceTags({ sources, labels }: { sources?: TabKey[]; labels?: string[] }) {
  const names = [...(sources ?? []).map((s) => TAB_LABELS[s]), ...(labels ?? [])];
  if (names.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="text-xs text-gray-500">reads from</span>
      {names.map((name) => (
        <span
          key={name}
          className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

function Entry({
  entry,
  tabLabel,
}: {
  entry: FormulaEntry;
  /** Set only on a search hit, to say which tab it was found under. */
  tabLabel?: string;
}) {
  return (
    <div className="border-b border-gray-100 px-4 py-3 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-sm font-medium text-gray-900">{entry.label}</div>
        {tabLabel && (
          <span className="shrink-0 text-xs text-gray-400">{tabLabel}</span>
        )}
      </div>
      <div className="mt-1 whitespace-pre-line font-mono text-xs text-gray-700">{entry.formula}</div>
      {entry.fix && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-900">
          <span className="font-semibold">Fix {entry.fix.id}</span>
          <span>{entry.fix.title}</span>
        </div>
      )}
      <SourceTags sources={entry.sources} labels={entry.sourceLabels} />
      {entry.note && (
        <p className="mt-1.5 text-xs text-gray-500">{entry.note}</p>
      )}
    </div>
  );
}

export function FormulasPanelV2({
  open,
  onClose,
  groups,
  subtitle = 'Every computed line, and the tabs it reads from',
  footer = 'Transcribed from the engine, not the spec — the engine decides the number.',
  placeholder = 'Search every tab — e.g. EBITDA, stock, VAT',
}: {
  /** What this discipline's formulas are, in a line - they differ in kind, not only in content. */
  subtitle?: string;
  footer?: string;
  placeholder?: string;
  /**
   * Which formulas to show, chosen by the caller.
   *
   * A PROP rather than read from either discipline's module. This file imported the
   * accounting list directly and, once it learned about the valuation one, was importing
   * across the boundary - which `boundary.test.ts` refused, correctly. Taking the groups
   * removes the crossing rather than granting an exemption for it, and leaves this a
   * renderer of whatever it is handed.
   */
  groups: FormulaGroup[];
  open: boolean;
  onClose: () => void;
}) {
  const keys = groups.map((group) => group.key);
  const byKey = new Map(groups.map((group) => [group.key, group]));
  const sectionsFor = (key: string) => byKey.get(key)?.sections ?? [];
  const labelFor = (key: string) => byKey.get(key)?.label ?? key;
  const [tab, setTab] = useState<string>('pnl');

  // A discipline switch while the panel is open would otherwise leave it on a tab the
  // other discipline does not have, and `sectionsFor` would return undefined.
  const active = keys.includes(tab) ? tab : keys[0];
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  // A search crosses every tab; without a search you get the tab you picked.
  const hits = useMemo(() => {
    if (!q) return null;
    const out: { entry: FormulaEntry; tab: string }[] = [];
    for (const key of keys) {
      for (const section of sectionsFor(key)) {
        for (const entry of section.entries) {
          const hay = `${entry.label} ${entry.formula} ${entry.note ?? ''} ${entry.fix ? `fix ${entry.fix.id} ${entry.fix.title}` : ''}`.toLowerCase();
          if (hay.includes(q)) out.push({ entry, tab: key });
        }
      }
    }
    return out;
  }, [q, groups]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Formula reference"
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Formulas</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {subtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-lg leading-none text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          </div>

          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="mt-3 w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />

          {!q && (
            <nav className="mt-3 flex flex-wrap gap-1">
              {keys.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  aria-pressed={tab === key}
                  className={[
                    'rounded border px-2 py-1 text-xs',
                    tab === key
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  {labelFor(key)}
                </button>
              ))}
            </nav>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {hits ? (
            hits.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500">
                Nothing matches “{query}”.
              </p>
            ) : (
              <div>
                <p className="px-4 pt-3 text-xs text-gray-500">
                  {hits.length} {hits.length === 1 ? 'line' : 'lines'} across all tabs
                </p>
                {hits.map(({ entry, tab: hitTab }) => (
                  <Entry
                    key={`${hitTab}-${entry.label}`}
                    entry={entry}
                    tabLabel={labelFor(hitTab)}
                  />
                ))}
              </div>
            )
          ) : (
            sectionsFor(active).map((section) => (
              <section key={section.section}>
                <h3 className="sticky top-0 border-b border-gray-200 bg-gray-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {section.section}
                </h3>
                {section.entries.map((entry) => (
                  <Entry key={entry.label} entry={entry} />
                ))}
              </section>
            ))
          )}
        </div>

        <p className="shrink-0 border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
          {footer}
        </p>
      </div>
    </div>
  );
}
